import { useState } from "react";
import { PHONE_CALL_SUCCESS } from "../../../skills/leadcue-reply/scripts/lib/phone-call.ts";
import { bookingApplies } from "../src/campaign-list.ts";
import {
  ACTION_LABEL,
  ADMISSION_HINT,
  ADMISSION_LABEL,
  ADMISSION_POLICIES,
  type AdmissionPolicy,
  ALLOWED_ACTION_LABEL,
  ALWAYS_PERMITTED,
  type AllowedAction,
  CALENDAR_HINT,
  CALENDAR_LABEL,
  type CalendarMode,
  type CampaignAction,
  chosen,
  GOAL_KINDS,
  GOAL_LABEL,
  type GoalKind,
  OFFERED_ACTIONS,
  OFFERED_CALENDAR_MODES,
  OPTIONAL_ACTIONS,
  OWNER_LABEL,
  OWNERS,
  type Owner,
  PARTICIPANT_ROLES,
  type ParticipantRole,
  ROLE_LABEL,
  SIGN_OFF_LABEL,
  SIGN_OFF_MODES,
  type SignOffMode,
} from "./settings-vocabulary.ts";

/*
 * The form a rep states this workspace's settings in.
 *
 * Every campaign of the workspace inherits these, including campaigns Bison has not started yet, so
 * the form is the one place where the shape of the whole workspace's drafting is decided. It states
 * content only: `configuration_revision` belongs to the host and the route refuses a body carrying
 * one (`workspace-settings-input.ts` says why).
 *
 * The controls that a schema refinement would refuse are not offered. A policy must permit
 * acknowledge and request clarification, so those two are shown as permanently on rather than as
 * choices that fail on save; a role belongs to an event goal and no other, so it appears with that
 * goal and disappears with it.
 */

export type GoalDraft = {
  kind: GoalKind;
  target: string;
  success_criterion: string;
  participant_role: ParticipantRole | null;
};

export type SettingsDraft = {
  goal: GoalDraft | null;
  policy: {
    permitted_actions: CampaignAction[];
    owner: Owner;
    handoff_contact?: {
      workspace_id: number;
      contact_id: string;
      name: string;
      email: string;
    } | null;
  } | null;
  bisonWorkspaceId?: number;
  calendarMode: CalendarMode;
  bookingUrl: string;
  admissionPolicy: AdmissionPolicy;
  allowedActions: AllowedAction[];
  signOffMode: SignOffMode | null;
};

/**
 * The draft as it goes to the route.
 *
 * The two always-permitted actions are shown ticked and disabled, so they have to be in what is
 * sent. Without this the form offered a policy `nextStepPolicySchema` refuses, and the refusal named
 * the two boxes the rep could see were already on. Found by saving the form in a browser, not by a
 * test: the checkbox state and the draft were two descriptions of one thing, and only one of them
 * was ever sent. It is a function of its own so that it has a falsifier.
 */
export function settingsToSave(draft: SettingsDraft): SettingsDraft {
  const booked = bookingForGoal(draft);
  if (booked.policy === null) return booked;
  const optional = booked.policy.permitted_actions.filter(
    (action) => !ALWAYS_PERMITTED.some((always) => always === action),
  );
  return {
    ...booked,
    policy: { ...booked.policy, permitted_actions: [...ALWAYS_PERMITTED, ...optional] },
  };
}

/**
 * A goal no booking serves hides the booking choice, so wherever its stored setting would be refused,
 * a retired "by hand" among them, it saves a connected calendar with none bound, which drafts as
 * `manual` did. A setting that is already valid is kept as it is.
 */
function bookingForGoal(draft: SettingsDraft): SettingsDraft {
  if (bookingApplies(draft.goal?.kind ?? null)) return draft;
  if (bookingFieldProblem(draft.calendarMode, draft.bookingUrl) === null) return draft;
  return { ...draft, calendarMode: "connector", bookingUrl: "" };
}

/**
 * What is wrong with this draft, in the app's own words, or null.
 *
 * The form carries `noValidate` and asks this instead, because the browser's built-in validation
 * speaks the browser's language: a booking link of "calendly dot com" was refused with "Wprowadz
 * adres URL." inside an English interface, measured in a browser on 2026-09-21. That is the same
 * defect as the FAQ dates that rendered as "21 wrz 2026", and it has the same fix: the app owns the
 * words. The rules here are the route's rules, checked before a round trip rather than instead of
 * it.
 */
export function settingsProblem(draft: SettingsDraft): string | null {
  if (draft.goal !== null) {
    if (draft.goal.target.trim() === "") return "Say what a reply should lead to, in your words.";
    if (draft.goal.success_criterion.trim() === "")
      return "Say what counts as this conversation having worked.";
  }
  if (
    draft.policy?.permitted_actions.includes("handoff") &&
    (!draft.policy.handoff_contact?.name.trim() ||
      !draft.policy.handoff_contact.email.includes("@"))
  )
    return "Confirm the handoff contact name and email.";
  if (!bookingApplies(draft.goal?.kind ?? null)) return null;
  return bookingFieldProblem(draft.calendarMode, draft.bookingUrl);
}

/** What a save is refused with while the booking mode is one a rep can no longer choose. */
export const BOOKING_UNSET = "Pick Booking link or Connected calendar before you save.";

/**
 * What is wrong with how a time gets booked, or null: the workspace form and a campaign's own
 * booking ask the same thing, so they refuse in the same words.
 */
export function bookingFieldProblem(mode: CalendarMode, bookingUrl: string): string | null {
  if (chosen(mode, OFFERED_CALENDAR_MODES) === undefined) return BOOKING_UNSET;
  const link = bookingUrl.trim();
  // A connected calendar may carry its booking page too; it is optional there, never malformed.
  if (mode !== "booking_link_only" && link === "") return null;
  if (link === "") return "A booking-link campaign needs its link.";
  if (!URL.canParse(link) || new URL(link).protocol !== "https:")
    return "The booking link must be a full https address, like https://calendly.com/you/intro.";
  return null;
}

/** A labelled control, the shape every field on this page takes. */
export function Field(props: {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly children: React.ReactNode;
}) {
  return (
    <label className="lcr-settings-field" htmlFor={props.id}>
      <span className="lcr-settings-field-label">{props.label}</span>
      {props.children}
      {props.hint !== undefined && <span className="lcr-settings-hint">{props.hint}</span>}
    </label>
  );
}

/** The goal's own fields, shared by the workspace form and a campaign's override of it. */
export function GoalFields({
  goal,
  onChange,
  idPrefix,
  disabled,
  phoneCall,
}: {
  readonly goal: GoalDraft;
  readonly onChange: (next: GoalDraft) => void;
  readonly idPrefix: string;
  readonly disabled: boolean;
  /**
   * Whether phone calls run in this workspace, and whether the saved goal is one already. The goal is
   * offered where they run, and kept where it is held so the select never shows a value it lacks.
   */
  readonly phoneCall: { readonly on: boolean; readonly held: boolean };
}) {
  const kinds = GOAL_KINDS.filter(
    (kind) => kind !== "phone_call" || phoneCall.on || phoneCall.held,
  );
  // The phone goal's note says what choosing it does, or why a held one drafts nothing; a screen
  // reader hears it with the select rather than only when it reaches the paragraph.
  const noteId = `${idPrefix}-kind-note`;
  return (
    <>
      {/* The select and its phone note as one group, spaced like a field and its own hint. */}
      <div className="lcr-goal-kind">
        <Field id={`${idPrefix}-kind`} label="What a reply should lead to">
          <select
            id={`${idPrefix}-kind`}
            value={goal.kind}
            disabled={disabled}
            {...(goal.kind === "phone_call" ? { "aria-describedby": noteId } : {})}
            onChange={(event) => {
              const kind = chosen(event.target.value, GOAL_KINDS);
              if (kind === undefined) return;
              // A role belongs to an event goal and to no other; the schema refuses either mismatch.
              onChange({
                ...goal,
                kind,
                success_criterion:
                  kind === "phone_call" ? PHONE_CALL_SUCCESS : goal.success_criterion,
                participant_role:
                  kind === "event_participation" ? (goal.participant_role ?? "attendee") : null,
              });
            }}
          >
            {kinds.map((value) => (
              <option key={value} value={value}>
                {GOAL_LABEL[value]}
              </option>
            ))}
          </select>
        </Field>
        {goal.kind === "phone_call" &&
          (phoneCall.on ? (
            <p className="lcr-settings-hint" id={noteId}>
              Saving this goal enables phone agreement and number collection, without scheduling or
              calendar events. Call details stay in the app for manual handling.
            </p>
          ) : (
            <p className="lcr-settings-hint" id={noteId} data-tone="hold">
              Phone calls are not switched on for this workspace yet, so replies to a campaign with
              this goal wait for you and get no draft.
            </p>
          ))}
      </div>
      {goal.kind === "event_participation" && (
        <Field id={`${idPrefix}-role`} label="They take part as">
          <select
            id={`${idPrefix}-role`}
            value={goal.participant_role ?? "attendee"}
            disabled={disabled}
            onChange={(event) => {
              const role = chosen(event.target.value, PARTICIPANT_ROLES);
              if (role !== undefined) onChange({ ...goal, participant_role: role });
            }}
          >
            {PARTICIPANT_ROLES.map((value) => (
              <option key={value} value={value}>
                {ROLE_LABEL[value]}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field
        id={`${idPrefix}-target`}
        label="In your words"
        hint="What a draft is working towards, as you would say it to the lead."
      >
        <input
          id={`${idPrefix}-target`}
          type="text"
          value={goal.target}
          disabled={disabled}
          placeholder={
            goal.kind === "phone_call" ? "What the phone call is about" : "A 25-minute product demo"
          }
          onChange={(event) => onChange({ ...goal, target: event.target.value })}
        />
      </Field>
      <Field
        id={`${idPrefix}-criterion`}
        label={goal.kind === "phone_call" ? "Ready when" : "Counted as done when"}
        hint="The thing that has to happen for this conversation to have worked."
      >
        <input
          id={`${idPrefix}-criterion`}
          type="text"
          value={goal.kind === "phone_call" ? PHONE_CALL_SUCCESS : goal.success_criterion}
          disabled={disabled || goal.kind === "phone_call"}
          placeholder="A confirmed calendar booking"
          onChange={(event) => onChange({ ...goal, success_criterion: event.target.value })}
        />
      </Field>
    </>
  );
}

/** A booking mode's words; one still stored as booked by hand reads as work left to do. */
export function BookingModeLabel({ mode }: { readonly mode: CalendarMode }) {
  if (chosen(mode, OFFERED_CALENDAR_MODES) !== undefined) return <>{CALENDAR_LABEL[mode]}</>;
  return <span className="lcr-booking-not-set">{CALENDAR_LABEL[mode]}</span>;
}

/**
 * The booking method and its link, which travel together: a link-only campaign needs its link.
 *
 * A rep chooses a booking link or a connected calendar. A setting still stored as booked by hand,
 * which is retired, shows a card saying so above a select that reads "Choose one" until a choice is
 * made, so the form never sends the retired value back. `problem` is the refusal of the last save,
 * shown under the field it is about rather than at the foot of the form.
 */
export function BookingFields({
  mode,
  bookingUrl,
  onMode,
  onUrl,
  idPrefix,
  disabled,
  problem = null,
}: {
  readonly mode: SettingsDraft["calendarMode"];
  readonly bookingUrl: string;
  readonly onMode: (next: SettingsDraft["calendarMode"]) => void;
  readonly onUrl: (next: string) => void;
  readonly idPrefix: string;
  readonly disabled: boolean;
  readonly problem?: string | null;
}) {
  const offered = chosen(mode, OFFERED_CALENDAR_MODES);
  const unsetId = `${idPrefix}-calendar-unset`;
  const problemId = `${idPrefix}-calendar-problem`;
  const linkProblem = problem !== null && offered !== undefined && problem !== BOOKING_UNSET;
  const describedBy = [
    offered === undefined ? unsetId : null,
    problem !== null && !linkProblem ? problemId : null,
  ].filter((id) => id !== null);
  return (
    <div className="lcr-booking-fields">
      <div className="lcr-booking-choice">
        {offered === undefined && (
          <div className="lcr-booking-unset">
            <strong className="lcr-booking-unset-title">Choose how leads book</strong>
            <span id={unsetId}>
              Booking by hand is no longer offered. Pick Booking link and add your link, or pick
              Connected calendar.
            </span>
          </div>
        )}
        <Field
          id={`${idPrefix}-calendar`}
          label="How a time gets booked"
          {...(offered === undefined ? {} : { hint: CALENDAR_HINT[offered] })}
        >
          <select
            id={`${idPrefix}-calendar`}
            value={mode}
            disabled={disabled}
            aria-invalid={problem !== null && !linkProblem}
            {...(describedBy.length === 0 ? {} : { "aria-describedby": describedBy.join(" ") })}
            onChange={(event) => {
              const next = chosen(event.target.value, OFFERED_CALENDAR_MODES);
              if (next !== undefined) onMode(next);
            }}
          >
            {offered === undefined && (
              <option value={mode} disabled>
                Choose one
              </option>
            )}
            {OFFERED_CALENDAR_MODES.map((value) => (
              <option key={value} value={value}>
                {CALENDAR_LABEL[value]}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {offered !== undefined && (
        <Field
          id={`${idPrefix}-booking-url`}
          label={offered === "connector" ? "Booking page" : "Booking link"}
          {...(offered === "connector"
            ? { hint: "The page leads book on. A reply that arranges a call shares it." }
            : {})}
        >
          <input
            id={`${idPrefix}-booking-url`}
            type="url"
            value={bookingUrl}
            disabled={disabled}
            placeholder="https://"
            aria-invalid={linkProblem}
            {...(linkProblem ? { "aria-describedby": problemId } : {})}
            onChange={(event) => onUrl(event.target.value)}
          />
        </Field>
      )}
      {problem !== null && (
        <p className="lcr-settings-error" id={problemId} role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}

export function AdmissionField({
  value,
  onChange,
  idPrefix,
  disabled,
}: {
  readonly value: SettingsDraft["admissionPolicy"];
  readonly onChange: (next: SettingsDraft["admissionPolicy"]) => void;
  readonly idPrefix: string;
  readonly disabled: boolean;
}) {
  return (
    <Field
      id={`${idPrefix}-admission`}
      label="Which replies get drafted"
      hint={ADMISSION_HINT[value]}
    >
      <select
        id={`${idPrefix}-admission`}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          const next = chosen(event.target.value, ADMISSION_POLICIES);
          if (next !== undefined) onChange(next);
        }}
      >
        {ADMISSION_POLICIES.map((option) => (
          <option key={option} value={option}>
            {ADMISSION_LABEL[option]}
          </option>
        ))}
      </select>
    </Field>
  );
}

/** What a draft may propose, and what this product may do in Bison. */
function PolicyFields({
  draft,
  onChange,
  disabled,
}: {
  readonly draft: SettingsDraft;
  readonly onChange: (next: SettingsDraft) => void;
  readonly disabled: boolean;
}) {
  const policy = draft.policy;
  if (policy === null) return null;
  const contact = policy.handoff_contact;
  const toggle = (action: CampaignAction, on: boolean) =>
    onChange({
      ...draft,
      policy: {
        ...policy,
        handoff_contact:
          action === "handoff"
            ? on
              ? (policy.handoff_contact ?? {
                  workspace_id: draft.bisonWorkspaceId ?? 0,
                  contact_id: crypto.randomUUID(),
                  name: "",
                  email: "",
                })
              : null
            : (policy.handoff_contact ?? null),
        permitted_actions: on
          ? [...policy.permitted_actions, action]
          : policy.permitted_actions.filter((current) => current !== action),
      },
    });
  return (
    <>
      <Field
        id="lcr-settings-owner"
        label="Who owns the next step"
        hint="Whose answer a draft waits for when a conversation needs a decision."
      >
        <select
          id="lcr-settings-owner"
          value={policy.owner}
          disabled={disabled}
          onChange={(event) => {
            const owner = chosen(event.target.value, OWNERS);
            if (owner !== undefined) onChange({ ...draft, policy: { ...policy, owner } });
          }}
        >
          {OWNERS.map((value) => (
            <option key={value} value={value}>
              {OWNER_LABEL[value]}
            </option>
          ))}
        </select>
      </Field>
      <fieldset className="lcr-settings-set">
        <legend className="lcr-settings-set-title">What a draft may propose</legend>
        <p className="lcr-settings-hint">
          Acknowledging a reply and asking for clarification are always allowed, so a conversation
          that needs more information is never pushed towards the goal instead.
        </p>
        <ul className="lcr-settings-checks">
          {ALWAYS_PERMITTED.map((action) => (
            <li key={action}>
              <label className="lcr-settings-check">
                <input type="checkbox" checked disabled readOnly />
                <span>{ACTION_LABEL[action]}</span>
              </label>
            </li>
          ))}
          {OPTIONAL_ACTIONS.filter(
            (action) =>
              draft.goal?.kind !== "phone_call" ||
              !["offer_meeting", "share_booking_link", "manage_booking"].includes(action),
          ).map((action) => (
            <li key={action}>
              <label className="lcr-settings-check">
                <input
                  type="checkbox"
                  checked={policy.permitted_actions.includes(action)}
                  disabled={disabled}
                  onChange={(event) => toggle(action, event.target.checked)}
                />
                <span>{ACTION_LABEL[action]}</span>
              </label>
            </li>
          ))}
        </ul>
      </fieldset>
      {policy.permitted_actions.includes("handoff") && contact && (
        <fieldset className="lcr-settings-set">
          <legend>Approved handoff contact</legend>
          <Field id="lcr-handoff-name" label="Name">
            <input
              id="lcr-handoff-name"
              value={contact.name}
              disabled={disabled}
              onChange={(event) =>
                onChange({
                  ...draft,
                  policy: { ...policy, handoff_contact: { ...contact, name: event.target.value } },
                })
              }
            />
          </Field>
          <Field id="lcr-handoff-email" label="Email">
            <input
              id="lcr-handoff-email"
              type="email"
              value={contact.email}
              disabled={disabled}
              onChange={(event) =>
                onChange({
                  ...draft,
                  policy: { ...policy, handoff_contact: { ...contact, email: event.target.value } },
                })
              }
            />
          </Field>
        </fieldset>
      )}
    </>
  );
}

/*
 * What this product may do beyond drafting, on the rep's approval. Stopping, unsubscribing or
 * blocking a lead is Email Bison's job and is never offered (AGENTS.md; Dawid, 2026-09-28).
 */
function ActionsField({
  draft,
  onChange,
  disabled,
}: {
  readonly draft: SettingsDraft;
  readonly onChange: (next: SettingsDraft) => void;
  readonly disabled: boolean;
}) {
  return (
    <fieldset className="lcr-settings-set">
      <legend className="lcr-settings-set-title">Beyond drafting</legend>
      <p className="lcr-settings-hint">
        Left off, this product prepares replies and nothing else. When on, it still asks for your
        approval on each conversation. Stopping, unsubscribing or blocking a lead stays in Email
        Bison.
      </p>
      <ul className="lcr-settings-checks">
        {OFFERED_ACTIONS.map((value) => {
          const label = ALLOWED_ACTION_LABEL[value];
          return (
            <li key={value}>
              <label className="lcr-settings-check">
                <input
                  type="checkbox"
                  checked={draft.allowedActions.includes(value)}
                  disabled={disabled}
                  onChange={(event) =>
                    onChange({
                      ...draft,
                      allowedActions: event.target.checked
                        ? [...draft.allowedActions, value]
                        : draft.allowedActions.filter((current) => current !== value),
                    })
                  }
                />
                <span>{label}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}

/**
 * The whole workspace form.
 *
 * `onSave` is given the draft; the caller turns it into the route's body, because the caller is the
 * one holding the workspace id and the query cache.
 */
export function WorkspaceSettingsForm({
  initial,
  saving,
  error,
  onSave,
  onCancel,
  phoneCallOn,
}: {
  readonly initial: SettingsDraft;
  /** Whether phone calls run in this workspace (`phoneCall` on the settings read). */
  readonly phoneCallOn: boolean;
  readonly saving: boolean;
  readonly error: string | null;
  readonly onSave: (draft: SettingsDraft) => void;
  readonly onCancel: () => void;
}) {
  const [draft, setDraftState] = useState<SettingsDraft>(initial);
  const [problem, setProblem] = useState<string | null>(null);
  // An edit answers the last refusal, so the refusal goes with it rather than outliving the fix.
  const setDraft = (next: SettingsDraft): void => {
    if (next.goal?.kind === "phone_call" && draft.goal?.kind !== "phone_call")
      next = {
        ...next,
        calendarMode: "connector",
        bookingUrl: "",
        allowedActions: [],
        policy: {
          ...next.policy,
          owner: next.policy?.owner ?? "agency",
          permitted_actions: [
            "acknowledge",
            "answer",
            "request_clarification",
            "collect_phone_call",
          ],
          handoff_contact: null,
        },
      };
    setDraftState(next);
    setProblem(null);
  };
  const bookingProblem =
    problem !== null && problem === bookingFieldProblem(draft.calendarMode, draft.bookingUrl)
      ? problem
      : null;
  const goal = draft.goal;
  return (
    <form
      className="lcr-settings-form"
      // Our own checks, in our own words: see `settingsProblem`.
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const wrong = settingsProblem(draft);
        setProblem(wrong);
        if (wrong !== null) {
          if (wrong === bookingFieldProblem(draft.calendarMode, draft.bookingUrl))
            document
              .getElementById(
                draft.calendarMode === "booking_link_only"
                  ? "lcr-settings-booking-url"
                  : "lcr-settings-calendar",
              )
              ?.focus();
          return;
        }
        /*
         * The two always-permitted actions are shown ticked and disabled, so they have to be in
         * what is sent. Without this the form offered a policy the schema refuses, and the refusal
         * named the two boxes the rep could see were already on. Found in the browser, not in a
         * test: the draft and the checkboxes were two descriptions of one thing.
         */
        onSave(settingsToSave(draft));
      }}
    >
      {goal === null ? (
        <p className="lcr-settings-note">
          This workspace has no goal yet.{" "}
          <button
            type="button"
            className="lcr-small-button"
            onClick={() =>
              setDraft({
                ...draft,
                goal: {
                  kind: "meeting",
                  target: "",
                  success_criterion: "",
                  participant_role: null,
                },
              })
            }
          >
            Set one
          </button>
        </p>
      ) : (
        <GoalFields
          goal={goal}
          disabled={saving}
          idPrefix="lcr-settings-goal"
          phoneCall={{ on: phoneCallOn, held: initial.goal?.kind === "phone_call" }}
          onChange={(next) => setDraft({ ...draft, goal: next })}
        />
      )}
      {bookingApplies(draft.goal?.kind ?? null) && (
        <BookingFields
          mode={draft.calendarMode}
          bookingUrl={draft.bookingUrl}
          disabled={saving}
          idPrefix="lcr-settings"
          problem={bookingProblem}
          onMode={(calendarMode) => setDraft({ ...draft, calendarMode })}
          onUrl={(bookingUrl) => setDraft({ ...draft, bookingUrl })}
        />
      )}
      <AdmissionField
        value={draft.admissionPolicy}
        disabled={saving}
        idPrefix="lcr-settings"
        onChange={(admissionPolicy) => setDraft({ ...draft, admissionPolicy })}
      />
      <Field
        id="lcr-settings-sign-off"
        label="How drafts sign off, unless a campaign says otherwise"
      >
        <select
          id="lcr-settings-sign-off"
          value={draft.signOffMode ?? "first_name"}
          disabled={saving}
          onChange={(event) => {
            const mode = chosen(event.target.value, SIGN_OFF_MODES);
            if (mode !== undefined) setDraft({ ...draft, signOffMode: mode });
          }}
        >
          {SIGN_OFF_MODES.map((value) => (
            <option key={value} value={value}>
              {SIGN_OFF_LABEL[value]}
            </option>
          ))}
        </select>
      </Field>
      <PolicyFields draft={draft} disabled={saving} onChange={setDraft} />
      {draft.goal?.kind !== "phone_call" && (
        <ActionsField draft={draft} disabled={saving} onChange={setDraft} />
      )}
      {bookingProblem === null && (problem ?? error) !== null && (
        <p className="lcr-settings-error" role="alert">
          {problem ?? error}
        </p>
      )}
      <div className="lcr-actions">
        <button type="submit" className="lcr-small-button" disabled={saving}>
          {saving ? "Saving…" : "Save settings"}
        </button>
        <button type="button" className="lcr-small-button" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
